import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  MANUAL_STOP_LIMITS,
  customerContactFieldSchemas,
  type CustomerContactInput,
  type PreferredContactMethod
} from '@/lib/validation/manualStopValidation';
import { FieldError } from './FieldError';
import { formatPhoneAsTyped } from './formHelpers';

const PREFERRED_LABELS: Record<PreferredContactMethod, string> = {
  call: 'Phone call',
  text: 'Text message',
  email: 'Email'
};

type TextFieldName = 'first_name' | 'last_name' | 'phone_e164' | 'email';

interface CustomerContactFieldsProps {
  value: CustomerContactInput;
  onChange: (value: CustomerContactInput) => void;
  /** Errors from the last submit, keyed by field name (first_name, phone_e164, …). */
  errors: Record<string, string>;
}

/** Validate one contact field with the same rules the form uses on submit. */
function validateField(field: TextFieldName, v: string): string | undefined {
  const result = customerContactFieldSchemas[field].safeParse(v);
  return result.success ? undefined : result.error.issues[0]?.message;
}

interface TextFieldProps {
  field: TextFieldName;
  label: string;
  required?: boolean;
  value: string;
  error: string | undefined;
  onChange: (v: string) => void;
  onBlur: () => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const TextField: React.FC<TextFieldProps> = ({
  field,
  label,
  required,
  value,
  error,
  onChange,
  onBlur,
  inputProps
}) => {
  const id = `customer-${field}`;
  return (
    <div className='space-y-2'>
      <Label htmlFor={id}>
        {label} {required && <span className='text-destructive'>*</span>}
      </Label>
      <Input
        id={id}
        autoComplete='off'
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        className={cn(error && 'border-destructive')}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        {...inputProps}
      />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
};

/**
 * Customer name and contact inputs for a customer manual stop.
 * Each field is checked when the dispatcher leaves it, then live while they fix it.
 */
// eslint-disable-next-line max-lines-per-function -- one cohesive form section
export const CustomerContactFields: React.FC<CustomerContactFieldsProps> = ({
  value,
  onChange,
  errors
}) => {
  // Fields the dispatcher has left at least once → their live validation result.
  const [touched, setTouched] = useState<Partial<Record<TextFieldName, string | undefined>>>({});

  const errorFor = (field: TextFieldName) => (field in touched ? touched[field] : errors[field]);

  const handleChange = (field: TextFieldName, next: string) => {
    onChange({ ...value, [field]: next });
    if (field in touched) setTouched(t => ({ ...t, [field]: validateField(field, next) }));
  };

  const handleBlur = (field: TextFieldName) => {
    const current = (value[field] ?? '') as string;
    // Don't flag an untouched optional/empty field just for tabbing through it.
    if (!current && !(field in touched) && !errors[field]) return;
    setTouched(t => ({ ...t, [field]: validateField(field, current) }));
  };

  const textField = (
    field: TextFieldName,
    label: string,
    inputProps: React.InputHTMLAttributes<HTMLInputElement>,
    required = true
  ) => (
    <TextField
      field={field}
      label={label}
      required={required}
      value={(value[field] ?? '') as string}
      error={errorFor(field)}
      onChange={v =>
        handleChange(field, field === 'phone_e164' ? formatPhoneAsTyped(v, value.phone_e164) : v)
      }
      onBlur={() => handleBlur(field)}
      inputProps={inputProps}
    />
  );

  return (
    <div className='space-y-3'>
      <div className='grid grid-cols-2 gap-3'>
        {textField('first_name', 'First name', { maxLength: MANUAL_STOP_LIMITS.personName })}
        {textField('last_name', 'Last name', { maxLength: MANUAL_STOP_LIMITS.personName })}
      </div>

      {textField('phone_e164', 'Mobile phone', {
        type: 'tel',
        inputMode: 'tel',
        placeholder: '(512) 555-0123',
        maxLength: 20
      })}

      <div className='grid grid-cols-2 gap-3'>
        {textField(
          'email',
          'Email (optional)',
          { type: 'email', inputMode: 'email', maxLength: MANUAL_STOP_LIMITS.email },
          false
        )}
        <div className='space-y-2'>
          <Label htmlFor='customer-preferred-contact'>Preferred contact</Label>
          <Select
            value={value.preferred_contact}
            onValueChange={v =>
              onChange({ ...value, preferred_contact: v as PreferredContactMethod })
            }
          >
            <SelectTrigger id='customer-preferred-contact'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PREFERRED_LABELS) as PreferredContactMethod[]).map(method => (
                <SelectItem key={method} value={method}>
                  {PREFERRED_LABELS[method]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
